import { Box, Typography } from "@mui/joy";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GridChildComponentProps } from "react-window";
import mockBackground from "../../../assets/background.jpg";
import { BASE_URL } from "../../../config";
import { ILotto } from "../../../models";

const IMAGE_URL = `${BASE_URL}image/`;

export interface IGridCellProps extends GridChildComponentProps<ILotto[][]> {
  onView: (id: number) => void;
  showMaterialName?: boolean;
  columns: number;
}

export const LottoGridCell = ({
  columnIndex,
  rowIndex,
  style,
  data,
  showMaterialName,
  onView,
  columns,
}: IGridCellProps) => {
  const { i18n, t } = useTranslation();
  const item = useMemo(
    () => data[rowIndex][columnIndex],
    [data, rowIndex, columnIndex]
  );

  const pezzi = useMemo(
    () => (item?.dettagli ?? []).reduce((prev, item) => item.pezzi + prev, 0),
    [item]
  );
  const spessori = useMemo(
    () => (item?.dettagli ?? []).map((item) => item.misZ),
    [item]
  );
  const spessoreMin = useMemo(() => Math.min(...spessori), [spessori]);
  const spessoreMax = useMemo(() => Math.max(...spessori), [spessori]);

  const nomeMateriale = useMemo(() => {
    if (!item) return "";
    return i18n.language === "en" && item.qualita?.nomeEn
      ? item.qualita.nomeEn
      : item.qualita?.nome ?? "";
  }, [item, i18n]);

  if (!item) return null;

  const lastRow = data[data.length - 1];
  const filledCols = lastRow.filter(Boolean).length;

  const cover = item.images?.find((image) => image.isCover) ?? item.images?.[0];
  const background = cover ? `${IMAGE_URL}${cover.mdPath}` : mockBackground;

  let adjustedStyle = { ...style };
  if (rowIndex === data.length - 1 && filledCols < columns) {
    const cellWidth = (style as any).width ?? 0;
    const totalWidth = columns * cellWidth;
    const usedWidth = filledCols * cellWidth;
    const offset = (totalWidth - usedWidth) / 2;

    adjustedStyle = {
      ...style,
      left: (style as any).left + offset,
    };
  }
  const isFirst = columnIndex === 0;
  const isLast = columnIndex === columns - 1;

  return (
    <Box
      style={adjustedStyle}
      sx={{
        boxSizing: "border-box",
        position: "relative",
        padding: 2,
        cursor: "pointer",
        transition: "all 300ms ease",
        paddingLeft: isFirst ? "20px" : "16px",
        paddingRight: isLast ? "20px" : "16px",
        "&:hover": {
          transform: "translateY(-5px)",
          opacity: "0.8",
          ".bg-img": {
            transform: "scale(1.1)",
          },
        },
        "@media screen and (min-width: 1024px)": {
          paddingLeft: isFirst ? "50px" : "16px",
          paddingRight: isLast ? "50px" : "16px",
        },
      }}
      onClick={() => onView(item.id)}
    >
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            className="bg-img"
            sx={{
              backgroundImage: `url('${background}')`,
              transition: "all 300ms ease",
              width: "100%",
              height: "100%",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          ></Box>
        </Box>
        <Box
          sx={{
            position: "relative",
          }}
        >
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between" }}
              onClick={() => onView(item.id)}
            >
              <Typography level="h4" sx={{ color: "rgb(86, 185, 58)" }}>
                {showMaterialName
                  ? `${nomeMateriale} - ${item.codice}`
                  : item.codice}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                pr: 1,
              }}
            >
              <Box sx={{ display: "flex" }}>
                <Box sx={{ display: "flex", width: 60 }}>
                  <Typography level="body-xs">N.</Typography>
                  <Typography level="body-xs" sx={{ pl: 1, color: "#fff" }}>
                    {pezzi}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", width: 120 }}>
                  <Typography level="body-xs">{t("ThicknessShort")}</Typography>
                  {spessoreMax === spessoreMin && (
                    <Typography level="body-xs" sx={{ pl: 1, color: "#fff" }}>
                      {spessoreMax} cm
                    </Typography>
                  )}
                  {spessoreMax !== spessoreMin && (
                    <Typography level="body-xs" sx={{ pl: 1, color: "#fff" }}>
                      {t("From")} {spessoreMin} cm {t("To")} {spessoreMax} cm
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
